import { ClipboardCheck, Eye } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { SurveyAnswerModal } from '../../../learning-development/survey/components/SurveyAnswerModal';
import { describeTargets } from '../../../learning-development/survey/utils/describeTargets';
import { MY_SURVEY_ANSWER_SOURCE, mySurveysApi } from '../mySurveysApi';
import { MY_SURVEY_STAGE, MY_SURVEY_STAGE_LABELS, type MySurvey, type MySurveyAccount } from '../mySurveyTypes';

/**
 * Self Service › Learning & Development › Survey — the surveys that cover the signed-in employee.
 *
 * **IT LISTS ONLY THE CALLER'S SURVEYS, and nothing here says so to the server.** Which surveys
 * — those covering their unit and position, and any they have answered — is fixed by the ROUTE;
 * there is no scope parameter on the wire.
 *
 * **Nothing to create, edit or delete**: the survey is Learning & Development's. The one thing an
 * employee does here is ANSWER, and a row's menu offers it; the anchor opens the profile.
 */
export function MySurveysIndexPage() {
  const navigate = useNavigate();

  const [answering, setAnswering] = useState<MySurvey | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [account, setAccount] = useState<MySurveyAccount | null>(null);

  /*
    A refusal on the ACCOUNT is announced before it fires: an account linked to no employee
    record has no surveys and cannot answer one. Seeded met — a lookup that never answers
    states nothing.
  */
  useEffect(() => {
    const controller = new AbortController();

    mySurveysApi
      .me(controller.signal)
      .then(setAccount)
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      mySurveysApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback((row: MySurvey): MenuItem[] => {
    if (row.questionnaire == null) return [];

    return row.respondedAt != null
      ? [
          {
            key: 'view',
            label: 'View my answers',
            icon: <Eye className="h-4 w-4" aria-hidden="true" />,
            permission: 'my-surveys.read',
            onClick: () => setAnswering(row),
          },
        ]
      : [
          {
            key: 'answer',
            label: 'Answer',
            icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
            permission: 'my-surveys.respond',
            disabledReason:
              row.isActive && row.questionnaire.isActive
                ? null
                : 'This questionnaire is deleted, so it can no longer be answered.',
            onClick: () => setAnswering(row),
          },
        ];
  }, []);

  return (
    <>
      <IndexPage<MySurvey>
        module="my-surveys"
        breadcrumb={['Self Service', 'Learning & Development', 'Survey']}
        columns={COLUMNS}
        facets={[
          { key: 'stage', label: 'Stage' },
          { key: 'status', label: 'Status' },
        ]}
        /*
          Filed by STATUS — Active / Inactive, the shell's default (product call, 13 Oct 2026; it was
          the stage). So no `folderNavFacet` is passed. The stage stays a filter in the rail and a
          column on the row; a reader can still repoint the tabs to it from the rail's cog.
        */
        // The writes this surface makes are responses and their answers.
        // …and the files of a training report, which are rows of their own under the response.
        auditEntityTypes={['SurveyRespondent', 'SurveyAnswer', 'SurveyTrainingReportAttachment']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search surveys"
        emptyMessage="No surveys for you right now. A survey appears here when Learning & Development asks your unit or position."
        prerequisites={[
          {
            label: 'your employee record',
            met: account?.error == null,
            detail: account?.error ?? undefined,
            to: '/personnel',
            permission: 'personnel.update',
          },
        ]}
        onRowOpen={(row) => navigate(`/self-service/surveys/${row.id}`)}
        openPermission="my-surveys.read"
        rowActions={rowActions}
        /*
          The standard checkbox gutter, ON though the page has no delete or bulk action — the
          shell would otherwise switch it off. Product call, 09 Oct 2026: the employee's list
          looks like every other index page.
        */
        selectable
        otherInfo={(row) => row}
      />

      {answering?.questionnaire != null ? (
        <SurveyAnswerModal
          open
          surveyId={answering.id}
          surveyActive={answering.isActive}
          questionnaire={answering.questionnaire}
          source={MY_SURVEY_ANSWER_SOURCE}
          onClose={() => setAnswering(null)}
          onAnswered={() => {
            setAnswering(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<MySurvey>[] = [
  {
    key: 'title',
    header: 'Title',
    width: 360,
    flex: true,
    sortable: true,
    render: (row) => <TextCell value={row.title} />,
  },
  {
    // Why the survey is the reader's: two SETS on one line, an empty one reading "All …".
    key: 'divisionPosition',
    header: 'Units/Organization / Position',
    width: 360,
    summary: true,
    render: (row) => (
      <TextCell
        value={`${describeTargets(row.orgUnits, 'All units/organizations')} — ${describeTargets(row.positions, 'All positions')}`}
      />
    ),
  },
  {
    // Not sortable: a correlated lookup of the caller's own response per row.
    key: 'respondedAt',
    header: 'Responded',
    width: 180,
    summary: true,
    render: (row) => <DateCell value={row.respondedAt} fallback="—" />,
  },
  {
    // The LIFECYCLE, beside the survey's own live-or-deleted pill: they are different facts.
    key: 'stage',
    header: 'Stage',
    width: 160,
    stage: true,
    // Blank before assessment — product call: "Not answered" and "Answered" are not statuses.
    render: (row) =>
      row.stage >= MY_SURVEY_STAGE.Assessed ? (
        <StatusText tone="inactive">{MY_SURVEY_STAGE_LABELS[row.stage]}</StatusText>
      ) : null,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
